import { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Link, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import BranchFormModal from '../components/BranchFormModal';
import { PencilIcon, TrashIcon, PlusIcon } from '@heroicons/react/outline';
import { useLanguage } from '../contexts/LanguageContext';

export default function Branches() {
  const [branches, setBranches] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const { user } = useAuth();
  const location = useLocation();
  const { t } = useLanguage();
  
  // Get companyId from URL query parameters if present
  const queryParams = new URLSearchParams(location.search);
  const companyIdFromUrl = queryParams.get('companyId');

  useEffect(() => {
    fetchBranches();
    
    // Admin also needs to fetch companies for dropdown
    if (user.role === 'admin') {
      fetchCompanies();
    }
  }, [user.role, companyIdFromUrl]);

  const fetchBranches = async () => {
    try {
      setIsLoading(true);
      let url = '/api/branches';
      
      // Add companyId as a query parameter if present in URL
      if (companyIdFromUrl) {
        url = `/api/branches?companyId=${companyIdFromUrl}`;
      }
      
      const response = await api.get(url);
      setBranches(response.data);
    } catch (error) {
      console.error('Error fetching branches:', error);
      toast.error(t('branches.fetchError'));
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await api.get('/api/companies');
      setCompanies(response.data);
    } catch (error) {
      console.error('Error fetching companies:', error);
      toast.error(t('companies.fetchError'));
    }
  };

  const handleAddSubmit = async (values) => {
    try {
      // For company users, ensure branch is created for their company
      if (user.role === 'company') {
        values.companyId = user.companyId;
      } else if (companyIdFromUrl && user.role === 'admin') {
        // For admin with companyId from URL
        values.companyId = companyIdFromUrl;
      }
      
      await api.post('/api/branches', values);
      toast.success(t('branches.branchCreated'));
      setIsAddModalOpen(false);
      fetchBranches();
    } catch (error) {
      console.error('Error adding branch:', error);
      toast.error(error.response?.data?.message || t('branches.branchCreateFailed'));
    }
  };

  const handleEditSubmit = async (values) => {
    try {
      await api.put(`/api/branches/${selectedBranch.id}`, values);
      toast.success(t('branches.branchUpdated'));
      setIsEditModalOpen(false);
      fetchBranches();
    } catch (error) {
      console.error('Error updating branch:', error);
      toast.error(error.response?.data?.message || t('branches.branchUpdateFailed'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('branches.confirmDelete'))) {
      return;
    }
    
    try {
      await api.delete(`/api/branches/${id}`);
      toast.success(t('branches.branchDeleted'));
      fetchBranches();
    } catch (error) {
      console.error('Error deleting branch:', error);
      toast.error(error.response?.data?.message || t('branches.branchDeleteFailed'));
    }
  };

  const handleEditClick = (branch) => {
    setSelectedBranch(branch);
    setIsEditModalOpen(true);
  };

  if (isLoading) {
    return <div className="py-6 px-4 sm:px-6 lg:px-8">{t('common.loading')}</div>;
  }

  return (
    <div className="py-6 px-4 sm:px-6 lg:px-8">
      <div className="mb-6 flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-gray-900">{t('branches.title')}</h1>
        <button
          onClick={() => setIsAddModalOpen(true)}
          className="btn-primary flex items-center"
        >
          <PlusIcon className="mr-2 h-5 w-5" />
          {t('branches.addBranch')}
        </button>
      </div>

      {branches.length === 0 ? (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg p-6 text-center text-gray-500">
          {t('branches.noBranches')}
        </div>
      ) : (
        <div className="mt-8 flex flex-col">
          <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      {t('common.name')}
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      {t('common.email')}
                    </th>
                    {user.role === 'admin' && (
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        {t('branches.company')}
                      </th>
                    )}
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      {t('common.status')}
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      {t('common.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {branches.map((branch) => (
                    <tr key={branch.id}>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {branch.name}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {branch.email}
                      </td>
                      {user.role === 'admin' && (
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {branch.company?.name || 'N/A'}
                        </td>
                      )}
                      <td className="whitespace-nowrap px-3 py-4 text-sm">
                        <span
                          className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${
                            branch.isActive
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {branch.isActive ? t('branches.active') : t('branches.inactive')}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        <div className="flex space-x-2">
                          <Link
                            to={`/branches/${branch.id}`}
                            className="text-indigo-600 hover:text-indigo-900"
                            title={t('common.view')}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                            </svg>
                          </Link>
                          <button
                            onClick={() => handleEditClick(branch)}
                            className="text-blue-600 hover:text-blue-900"
                            title={t('common.edit')}
                          >
                            <PencilIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleDelete(branch.id)}
                            className="text-red-600 hover:text-red-900"
                            title={t('common.delete')}
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Add Branch Modal */}
      <BranchFormModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={handleAddSubmit}
        companies={companies}
        userRole={user.role}
        companyId={companyIdFromUrl}
      />

      {/* Edit Branch Modal */}
      {selectedBranch && (
        <BranchFormModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSubmit={handleEditSubmit}
          initialValues={selectedBranch}
          companies={companies}
          userRole={user.role}
        />
      )}
    </div>
  );
} 